import React, { useState } from 'react';
import { Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import { formatStringWithUnderscores, getTruncatedId } from 'lib/utils/formatters';

interface InfoData {
    propertyUnitId: string;
    companyName: string;
    propertyUnitAddress: string;
    propertyCategory: string;
    propertyUnitType: string;
    propertyUnitStatus: string;
}

interface InformationProps {
    infoData: InfoData;
    isLoading?: boolean;
}

// Helper to get badge class based on status
const getStatusBadgeClass = (status: string) => {
    switch (status?.toLowerCase()) {
        case 'public':
        case 'earnest_paid':
        case 'payment_pending':
        case 'deal':
        case 'sold':
        case 'rented':
            return 'badge rounded-pill bg-success';
        case 'rejected':
            return 'badge rounded-pill bg-danger';
        case 'pending':
        case 'archived':
            return 'badge rounded-pill bg-warning';
        case 'private':
            return 'badge rounded-pill bg-info';
        default:
            return 'badge rounded-pill bg-warning';
    }
};

const Information: React.FC<InformationProps> = ({ infoData, isLoading }) => {
    const [copiedId, setCopiedId] = useState<string | null>(null);

    // Function to copy ID to clipboard
    const copyToClipboard = async (fullId: string) => {
        try {
            await navigator.clipboard.writeText(fullId);
            setCopiedId(fullId);
            // Reset the tick icon after 1 second
            setTimeout(() => {
                setCopiedId(null);
            }, 1000);
        } catch (err) {
            console.error('Failed to copy ID:', err);
        }
    };

    if (isLoading) {
        return (
            <Card className="rounded-0">
                <CardHeader className="my-2">
                    <h6>Information</h6>
                </CardHeader>
                <CardBody>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100px' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </CardBody>
            </Card>
        );
    }

    return (
        <Card className="rounded-0">
            <CardHeader className="py-3">
                <h6 className="my-1">Information</h6>
            </CardHeader>
            <CardBody>
                <Row className="gy-3">
                    <Col xs={12} md={2} className="d-flex flex-column">
                        <span className="fw-semibold" style={{ fontSize: 16 }}>Property ID</span>
                        <div className="d-flex align-items-center gap-2">
                            <span className="text-muted" style={{ fontSize: 15 }}>{getTruncatedId(infoData?.propertyUnitId) || 'N/A'}</span>
                            <span
                                onClick={() => infoData?.propertyUnitId && copyToClipboard(infoData.propertyUnitId)}
                                style={{
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: copiedId === infoData?.propertyUnitId ? '#28a745' : '#6b7280',
                                    transition: 'color 0.2s ease'
                                }}
                                title={copiedId === infoData?.propertyUnitId ? 'Copied!' : 'Copy full ID'}
                            >
                                {copiedId === infoData?.propertyUnitId ? (
                                    <i className="ri-check-line"></i>
                                ) : (
                                    <i className="ri-file-copy-line"></i>
                                )}
                            </span>
                        </div>
                    </Col>
                    <Col xs={12} md={2} className="d-flex flex-column">
                        <span className="fw-semibold" style={{ fontSize: 16 }}>Builder</span>
                        <span className="text-muted" style={{ fontSize: 15 }}>{infoData?.companyName || 'N/A'}</span>
                    </Col>
                    <Col xs={12} md={2} className="d-flex flex-column">
                        <span className="fw-semibold" style={{ fontSize: 16 }}>Address</span>
                        <span className="text-muted" style={{ fontSize: 15 }}>{infoData?.propertyUnitAddress || 'N/A'}</span>
                    </Col>
                    <Col xs={12} md={2} className="d-flex flex-column">
                        <span className="fw-semibold" style={{ fontSize: 16 }}>Property Category</span>
                        <span className="text-muted" style={{ fontSize: 15 }}>{infoData?.propertyCategory || 'N/A'}</span>
                    </Col>
                    <Col xs={12} md={2} className="d-flex flex-column">
                        <span className="fw-semibold" style={{ fontSize: 16 }}>Property Type</span>
                        <span className="text-muted" style={{ fontSize: 15 }}>{infoData?.propertyUnitType || 'N/A'}</span>
                    </Col>
                    <Col xs={12} md={2} className="d-flex flex-column">
                        <span className="fw-semibold" style={{ fontSize: 16 }}>Status</span>
                        <div className="d-flex">
                            <span className={getStatusBadgeClass(infoData?.propertyUnitStatus)} >{formatStringWithUnderscores(infoData?.propertyUnitStatus) || 'N/A'}</span>
                        </div>
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );
}

export default Information;
